/**
 * Firebase email action links (`/auth/complete-reset`, `/reset-password`) carry their credential in
 * the query string: `oobCode` is single-use, but a password-reset code is an account takeover for
 * the hour it lives. These helpers keep it from lingering where it can be read back.
 */

/** Query parameters of an action link that must not be recorded anywhere. */
const SENSITIVE_PARAMS = ['oobCode', 'apiKey', 'mode'] as const;

const SENSITIVE_PARAM_PATTERN = new RegExp(`([?&](?:${SENSITIVE_PARAMS.join('|')})=)[^&#]*`, 'g');

/**
 * `url` with the value of every sensitive action-link parameter replaced by `[Filtered]`. Anything
 * that is not a string is returned unchanged, so it can be applied to event fields blindly.
 */
export const scrubAuthActionParams = <T>(url: T): T =>
  (typeof url === 'string' ? url.replace(SENSITIVE_PARAM_PATTERN, '$1[Filtered]') : url) as T;

/**
 * Drops the action code (and the API key beside it) from the address bar without adding a history
 * entry, once the page has read them, so they are no longer visible, bookmarked or recorded as the
 * page URL by anything that reads it later.
 *
 * `mode` is kept, so a reload still lands on the right screen (which then reports the missing code
 * as an invalid link). Router state (`history.state`) is preserved: React Router keeps its own
 * bookkeeping there, and has already read the query it renders from.
 */
export const clearActionCodeFromAddressBar = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('oobCode') && !url.searchParams.has('apiKey')) return;
  url.searchParams.delete('oobCode');
  url.searchParams.delete('apiKey');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};
